import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataText } from '../../../components/typography/data-text.tsx';
import { NumberOrEmpty } from '../../../components/typography/number-or-empty.tsx';
import { Checkbox } from '../../../components/ui/checkbox.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { GetProductAvailibilityByLocationListItem } from '../../../server/types/infer-trpc-types.ts';
import { DoubleDeckerCell } from '../components/double-decker-cell';

export const useProductAvailibilityColumns = (includeSelect = false) => {
    const { t, locale } = useLocale();

    const productAvailibilityColumns: ColumnDef<GetProductAvailibilityByLocationListItem>[] = useMemo(() => {
        const columns: ColumnDef<GetProductAvailibilityByLocationListItem>[] = [];

        if (includeSelect) {
            columns.push({
                id: 'select',
                cell: ({ row }) => (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={
                            row.getIsSelected()
                                ? undefined
                                : (value) => {
                                      row.toggleSelected(!!value);
                                  }
                        }
                        aria-label="Select row"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
                size: 30,
            });
        }

        columns.push(
            {
                accessorKey: 'storage_location_id',
                header: t('table_header__location', {
                    ns: 'table-product-availibity',
                }),
                cell: ({ cell }) => (
                    <DoubleDeckerCell
                        align="left"
                        topLevel={cell.getValue() as string}
                        bottomLevel={cell.row.original.storage_location_name}
                    />
                ),
                size: 150,
            },
            {
                accessorKey: 'quantity_unit_sales',
                header: t('table_header__unit', {
                    ns: 'table-product-availibity',
                }),
                size: 40,
            },
            {
                accessorKey: 'stock_quantity',
                header: t('table_header__inventory', {
                    ns: 'table-product-availibity',
                }),
                cell: ({ cell }) => (
                    <DataText>
                        <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'reserved',
                header: t('table_header__reserved', {
                    ns: 'table-product-availibity',
                }),
                cell: ({ cell }) => (
                    <DataText>
                        <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                    </DataText>
                ),
            },

            {
                accessorKey: 'picked',
                header: t('table_header__picked', {
                    ns: 'table-product-availibity',
                }),
                cell: ({ cell }) => (
                    <DataText>
                        <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                    </DataText>
                ),
            },

            {
                accessorKey: 'effective',
                header: t('table_header__effective_inventory', {
                    ns: 'table-product-availibity',
                }),
                cell: ({ cell }) => (
                    <DataText>
                        <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                    </DataText>
                ),
            },
        );

        return columns;
    }, [t, locale, includeSelect]);

    return productAvailibilityColumns;
};
